'use client';

import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import type { Analytics } from '@repo/analytics/analytics';
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Logo } from '@repo/design-system/components/ds/logo';
import { Form } from '@repo/design-system/components/ui/form';
import { Modal, type ModalProps } from '@repo/design-system/components/ui/modal';
import { PasswordInput } from '@repo/design-system/components/ui/password-input';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import type { AuthModalMode, WebAuthErrorCode, WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import type { HostType } from '@repo/types/host';
import type { LoginCredentials, RegisterCredentials } from '@repo/wemod-api-client/actions/auth-actions';
import { loginAction, registerAction } from '@repo/wemod-api-client/actions/auth-actions';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import authHeroBgDefault from '../../assets/images/auth-modal/auth-hero-default.webp';
type AuthLoginFn = (credentials: LoginCredentials) => Promise<void>;
type AuthRegisterFn = (credentials: RegisterCredentials) => Promise<void>;
type AuthLoginErrorMessageFn = (error: unknown) => string | undefined;
type AuthHeroCopyKey = Exclude<WebLoginPromptTrigger, 'save-explore-list' | 'save-hero-list'> | 'save-list';
function getAuthHeroCopyKey(trigger: WebLoginPromptTrigger): AuthHeroCopyKey {
  return trigger === 'save-explore-list' || trigger === 'save-hero-list' ? 'save-list' : trigger;
}
export interface AuthModalProps extends ModalProps {
  initialMode?: AuthModalMode;
  trigger?: WebLoginPromptTrigger;
  heroImageSrc?: string;
  analytics?: Pick<Analytics, 'event'>;
  platform?: HostType;
  onLogin?: AuthLoginFn;
  onRegister?: AuthRegisterFn;
  getLoginErrorMessage?: AuthLoginErrorMessageFn;
  onLoginSuccess?: () => void | Promise<void>;
  onRegisterSuccess?: () => void | Promise<void>;
  turnstileBypassToken?: string;
  headerAction?: React.ReactNode;
}
type FieldErrors = Partial<Record<'email' | 'password', string>>;
type AuthErrorKey = 'error_signing_up' | 'error_logging_in' | 'error_too_many_attempts' | 'error_server_unreachable';
function detectSignupOs(): 'windows' | 'nonwindows' {
  if (typeof navigator === 'undefined') return 'nonwindows';
  return /Windows/i.test(navigator.userAgent) ? 'windows' : 'nonwindows';
}
function extractErrorCode(err: unknown): WebAuthErrorCode {
  const code = (err as {
    errorCode?: unknown;
  })?.errorCode;
  return typeof code === 'string' ? code as WebAuthErrorCode : 'unknown';
}
function errorMessageKey(code: WebAuthErrorCode, fallback: 'error_signing_up' | 'error_logging_in'): AuthErrorKey {
  if (code === 'rate_limited') return 'error_too_many_attempts';
  if (code === 'server_error') return 'error_server_unreachable';
  return fallback;
}
export const AuthModal: React.FC<AuthModalProps> = ({
  initialMode = 'signup',
  isOpen,
  onOpenChange,
  trigger,
  heroImageSrc,
  analytics,
  platform,
  onLogin,
  onRegister,
  getLoginErrorMessage,
  onLoginSuccess,
  onRegisterSuccess,
  turnstileBypassToken,
  headerAction,
  size = 'sm'
}) => {
  const [mode, setMode] = useState<AuthModalMode>(initialMode);
  const [signupStep, setSignupStep] = useState<'email' | 'credentials'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const hasTrackedRef = useRef(false);
  const {
    t
  } = useT(['common', 'game_map']);
  const authHeroCopyKey = trigger ? getAuthHeroCopyKey(trigger) : undefined;
  const verificationToken = turnstileBypassToken ?? turnstileToken;
  const baseParams = useMemo(() => {
    if (!trigger || !platform) return null;
    return {
      trigger,
      url: typeof window !== 'undefined' ? window.location.pathname : '',
      platform
    };
  }, [trigger, platform]);
  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
    setTurnstileResetKey(key => key + 1);
  }, []);
  const resetFormFields = useCallback(() => {
    setEmail('');
    setPassword('');
    setPasswordConfirmation('');
    setError('');
    setFieldErrors({});
    setSignupStep('email');
    resetTurnstile();
  }, [resetTurnstile]);
  useEffect(() => {
    if (isOpen && !hasTrackedRef.current && baseParams) {
      hasTrackedRef.current = true;
      analytics?.event('web_login_prompt', baseParams);
    }
    if (!isOpen) {
      hasTrackedRef.current = false;
      setMode(initialMode);
      resetFormFields();
    }
  }, [isOpen, analytics, baseParams, resetFormFields, initialMode]);
  const switchMode = useCallback((toMode: AuthModalMode) => {
    const fromMode = mode;
    setMode(toMode);
    if (fromMode === 'login' && toMode === 'signup') {
      setEmail('');
    }
    setPassword('');
    setPasswordConfirmation('');
    setError('');
    setFieldErrors({});
    setSignupStep('email');
    resetTurnstile();
    if (baseParams) {
      analytics?.event('web_login_prompt_mode_switch', {
        ...baseParams,
        from_mode: fromMode,
        to_mode: toMode
      });
    }
  }, [mode, analytics, baseParams, resetTurnstile]);
  const defaultLogin: AuthLoginFn = async credentials => {
    const result = await loginAction(credentials);
    if (!result.success) {
      const err: Error & {
        errorCode?: WebAuthErrorCode;
      } = new Error(result.error);
      err.errorCode = result.errorCode;
      throw err;
    }
  };
  const performLogin = onLogin ?? defaultLogin;
  const defaultRegister: AuthRegisterFn = async credentials_0 => {
    const result_0 = await registerAction(credentials_0);
    if (!result_0.success) {
      const err_0: Error & {
        errorCode?: WebAuthErrorCode;
        fieldErrors?: Record<string, string[]>;
      } = new Error(result_0.error);
      err_0.errorCode = result_0.errorCode;
      err_0.fieldErrors = result_0.fieldErrors;
      throw err_0;
    }
  };
  const performRegister = onRegister ?? defaultRegister;
  const handleLogin = useCallback(async () => {
    if (!email || !password) {
      setError(t($ => $.please_enter_email_and_password));
      return;
    }
    if (!verificationToken) {
      setError(t($_0 => $_0.please_complete_verification));
      return;
    }
    setLoading(true);
    setError('');
    setFieldErrors({});
    if (baseParams) analytics?.event('web_login_attempt', baseParams);
    try {
      await performLogin({
        emailOrUsername: email,
        password,
        turnstileToken: verificationToken
      });
      if (baseParams) analytics?.event('web_login_success', baseParams);
      toast({
        title: t($_2 => $_2.login_successful),
        variant: 'success'
      });
      await onLoginSuccess?.();
      onOpenChange?.({
        open: false
      });
    } catch (err_1) {
      const code = extractErrorCode(err_1);
      if (baseParams) {
        analytics?.event('web_login_failure', {
          ...baseParams,
          error_code: code
        });
      }
      const key_0 = errorMessageKey(code, 'error_logging_in');
      setError(getLoginErrorMessage?.(err_1) ?? t($_1 => $_1[key_0]));
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }, [email, password, verificationToken, t, performLogin, onOpenChange, onLoginSuccess, getLoginErrorMessage, analytics, baseParams, resetTurnstile]);
  const handleSignup = useCallback(async () => {
    if (!email || !password) {
      setError(t($_3 => $_3.please_enter_email_and_password));
      return;
    }
    if (password !== passwordConfirmation) {
      setFieldErrors({
        password: t($_4 => $_4.passwords_must_match)
      });
      return;
    }
    if (!verificationToken) {
      setError(t($_5 => $_5.please_complete_verification));
      return;
    }
    setLoading(true);
    setError('');
    setFieldErrors({});
    if (baseParams) analytics?.event('web_signup_attempt', baseParams);
    try {
      await performRegister({
        email,
        password,
        passwordConfirmation,
        turnstileToken: verificationToken,
        agreement: true,
        signupOs: detectSignupOs()
      });
      if (baseParams) analytics?.event('web_signup_success', baseParams);
      toast({
        title: t($_8 => $_8.signup_successful),
        variant: 'success'
      });
      await onRegisterSuccess?.();
      onOpenChange?.({
        open: false
      });
    } catch (err_2) {
      const code_0 = extractErrorCode(err_2);
      if (baseParams) {
        analytics?.event('web_signup_failure', {
          ...baseParams,
          error_code: code_0
        });
      }
      const serverFieldErrors = (err_2 as {
        fieldErrors?: Record<string, string[]>;
      }).fieldErrors;
      if (serverFieldErrors) {
        setFieldErrors({
          email: serverFieldErrors.email?.[0],
          password: serverFieldErrors.password?.[0] ?? serverFieldErrors.password_confirmation?.[0]
        });
        if (serverFieldErrors.email?.[0]) {
          setSignupStep('email');
        }
        if (serverFieldErrors.turnstile_token?.[0] ?? serverFieldErrors['cf-turnstile-response']?.[0]) {
          setError(t($_6 => $_6.please_complete_verification));
        }
      } else {
        const key_1 = errorMessageKey(code_0, 'error_signing_up');
        setError(t($_7 => $_7[key_1]));
      }
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }, [email, password, passwordConfirmation, verificationToken, t, performRegister, onOpenChange, onRegisterSuccess, analytics, baseParams, resetTurnstile]);
  const handleContinue = useCallback(() => {
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!looksLikeEmail) {
      setFieldErrors({
        email: t($_9 => $_9.please_enter_valid_email)
      });
      return;
    }
    setError('');
    setFieldErrors({});
    if (baseParams) analytics?.event('web_signup_email_submit', baseParams);
    setSignupStep('credentials');
  }, [email, t, analytics, baseParams]);
  const handleSubmit = useCallback((event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mode === 'login') {
      void handleLogin();
      return;
    }
    if (signupStep === 'email') {
      handleContinue();
      return;
    }
    void handleSignup();
  }, [mode, signupStep, handleLogin, handleSignup, handleContinue]);
  const wandWebsiteUrl = process.env.NEXT_PUBLIC_WAND_WEBSITE_URL;
  const forgotPasswordUrl = `${wandWebsiteUrl}/forgot`;
  const termsUrl = `${wandWebsiteUrl}/terms`;
  const privacyUrl = `${wandWebsiteUrl}/privacy`;
  const siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
  const dividerStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: '16',
    width: 'full'
  });
  const dividerLineStyles = css({
    flex: '[1]',
    height: '[0px]',
    borderTop: '[1px solid]',
    borderColor: 'whiteAlpha.20'
  });
  const inputColumnStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '[12px]',
    width: 'full'
  });
  const inlineTermsStyles = css({
    color: 'whiteAlpha.60',
    fontSize: '[12px]',
    lineHeight: '[16px]',
    textAlign: 'center',
    width: 'full'
  });
  const generalErrorStyles = css({
    fontSize: '[14px]',
    color: '[#ff6b6b]',
    textAlign: 'center'
  });
  const externalLinkStyles = css({
    color: 'whiteAlpha.80',
    textDecoration: 'underline',
    _hover: {
      color: 'white'
    }
  });
  const backLinkStyles = css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4',
    maxWidth: 'full',
    background: 'transparent',
    border: 'none',
    color: 'whiteAlpha.60',
    cursor: 'pointer',
    fontSize: '[12px]',
    padding: '[0]',
    textDecoration: 'underline',
    alignSelf: 'flex-start',
    _hover: {
      color: 'whiteAlpha.80'
    }
  });
  const backLinkEmailStyles = css({
    minWidth: '[0]',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  });
  const turnstile = siteKey && isOpen && !turnstileBypassToken && <Turnstile key={turnstileResetKey} ref={turnstileRef} siteKey={typeof window !== 'undefined' && window.location.hostname.endsWith('.workers.dev') ? '1x00000000000000000000AA' : siteKey} onSuccess={token => setTurnstileToken(token)} onExpire={() => setTurnstileToken('')} onError={() => setTurnstileToken('')} options={{
    theme: 'dark',
    appearance: 'interaction-only',
    responseField: false,
    feedbackEnabled: false
  }} />;
  return <Modal isOpen={isOpen} onOpenChange={onOpenChange} size={size} variant="darkBlur">
            {headerAction && <div className={css({
      position: 'absolute',
      top: '16',
      left: '16',
      zIndex: 10
    })}>
                    {headerAction}
                </div>}
            <div className={css({
      position: 'absolute',
      inset: 'none',
      backgroundColor: 'white/2',
      borderRadius: '24',
      pointerEvents: 'none',
      zIndex: 0
    })} />
            <div className={css({
      position: 'absolute',
      top: 'none',
      left: 'none',
      width: 'full',
      height: '[200px]',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
      borderTopLeftRadius: '24',
      borderTopRightRadius: '24'
    })}>
                <img src={heroImageSrc ?? authHeroBgDefault.src} alt="" className={css({
        width: 'full',
        height: 'full',
        objectFit: 'cover',
        opacity: 0.5
      })} style={{
        maskImage: 'linear-gradient(to bottom, white, transparent)',
        WebkitMaskImage: 'linear-gradient(to bottom, white, transparent)'
      }} />
            </div>
            <Form ref={formRef} onSubmit={handleSubmit} tabIndex={0} className={css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16',
      position: 'relative',
      zIndex: 1,
      paddingTop: '40',
      paddingX: '24',
      paddingBottom: '24'
    })}>
                <Logo variant="wordmark" width="[107px]" color="brand.primary" aria-label="Wand" />

                <div className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4',
        textAlign: 'center',
        width: 'full'
      })}>
                    <Type variant="header-l" color="text.highlight">
                        {authHeroCopyKey ? t($_10 => $_10.auth_hero[authHeroCopyKey]?.title ?? $_10.auth_hero.default.title) : t($_11 => $_11.auth_hero.default.title)}
                    </Type>
                    {(() => {
          const subtitle = authHeroCopyKey ? t($_12 => $_12.auth_hero[authHeroCopyKey]?.subtitle ?? $_12.auth_hero.default.subtitle) : t($_13 => $_13.auth_hero.default.subtitle);
          return subtitle ? <Type variant="body-s" className={css({
            color: 'whiteAlpha.80'
          })}>
                                {subtitle}
                            </Type> : null;
        })()}
                </div>

                {error && <div className={generalErrorStyles}>{error}</div>}

                {mode === 'signup' ? <>
                        <div className={inputColumnStyles}>
                            {signupStep === 'email' ? <>
                                    <TextInput fullWidth placeholder={t($_14 => $_14.email)} value={email} onChange={e => setEmail(e.target.value)} disabled={loading} autoComplete="email" type="email" errorText={fieldErrors.email} />
                                    <Button type="submit" color="white" fullWidth disabled={loading}>
                                        {t($_15 => $_15.continue)}
                                    </Button>
                                    <div className={inlineTermsStyles}>
                                        <Trans i18nKey={$_16 => $_16.agree_to_terms_inline} ns="common" components={[<a key="terms" href={termsUrl} target="_blank" rel="noopener noreferrer" className={externalLinkStyles}>
                                                    {t($_17 => $_17.terms_and_conditions)}
                                                </a>, <a key="privacy" href={privacyUrl} target="_blank" rel="noopener noreferrer" className={externalLinkStyles}>
                                                    {t($_18 => $_18.privacy_policy)}
                                                </a>]} />
                                        <div className={css({
                marginTop: '[4px]'
              })}>
                                            {t($_19 => $_19.receive_occasional_emails)}
                                        </div>
                                    </div>
                                </> : <>
                                    <button type="button" className={backLinkStyles} onClick={() => setSignupStep('email')}>
                                        <Icon icon="arrow_back" size="sm" decorative />
                                        <span className={backLinkEmailStyles}>{email}</span>
                                    </button>
                                    <PasswordInput fullWidth value={password} placeholder={t($_20 => $_20.password)} onChange={e_0 => setPassword(e_0.target.value)} disabled={loading} errorText={fieldErrors.password} />
                                    <PasswordInput fullWidth value={passwordConfirmation} placeholder={t($_21 => $_21.confirm_password)} onChange={e_1 => setPasswordConfirmation(e_1.target.value)} disabled={loading} />
                                    <Button type="submit" color="white" fullWidth disabled={loading || !verificationToken}>
                                        {loading ? t($_22 => $_22.creating_account) : t($_23 => $_23.create_account)}
                                    </Button>
                                </>}
                        </div>

                        {turnstile}

                        <div className={dividerStyles}>
                            <div className={dividerLineStyles} />
                            <Type variant="body-s" className={css({
            color: 'whiteAlpha.60',
            fontSize: 'sm',
            whiteSpace: 'nowrap'
          })}>
                                {t($_24 => $_24.already_have_account)}
                            </Type>
                            <div className={dividerLineStyles} />
                        </div>

                        <Button type="button" onClick={() => switchMode('login')} color="secondary" fullWidth>
                            {t($_25 => $_25.log_in)}
                        </Button>
                    </> : <>
                        <div className={inputColumnStyles}>
                            <TextInput fullWidth placeholder={t($_26 => $_26.email_or_username)} value={email} onChange={e_2 => setEmail(e_2.target.value)} disabled={loading} autoComplete="username" errorText={fieldErrors.email} />
                            <PasswordInput fullWidth value={password} placeholder={t($_27 => $_27.password)} onChange={e_3 => setPassword(e_3.target.value)} disabled={loading} errorText={fieldErrors.password} />
                            <Button type="submit" color="white" fullWidth disabled={loading || !verificationToken}>
                                {loading ? t($_28 => $_28.logging_in) : t($_29 => $_29.login)}
                            </Button>
                            <Button type="button" variant="ghost" onClick={() => {
            if (baseParams) analytics?.event('web_forgot_password_click', baseParams);
            window.open(forgotPasswordUrl, '_blank');
          }} color="primary" fullWidth>
                                {t($_30 => $_30.forgot_password)}
                            </Button>
                        </div>

                        {turnstile}

                        <div className={dividerStyles}>
                            <div className={dividerLineStyles} />
                            <Type variant="body-s" className={css({
            color: 'whiteAlpha.60',
            fontSize: 'sm',
            whiteSpace: 'nowrap'
          })}>
                                {t($_31 => $_31.dont_have_an_account)}
                            </Type>
                            <div className={dividerLineStyles} />
                        </div>

                        <Button type="button" onClick={() => switchMode('signup')} color="secondary" fullWidth>
                            {t($_32 => $_32.sign_up)}
                        </Button>
                    </>}
            </Form>
        </Modal>;
};